import { CreationMethod } from '../enums';

export type CollectionAttributeType =
  | 'string'
  | 'number'
  | 'date'
  | 'boost_percentage'
  | 'boost_number';

export interface ICollectionLayerDto extends LayerConfig {
  fullPath: string;
}

export interface ICollectionUploadAssetDto {
  artCreator: string;
  creationMethod: CreationMethod;
  imgWidth: number;
  imgHeight: number;
  uploadedFilesCount: number;
  startEditionFrom: number;
}

export enum CollectionNamingConvention {
  EDITION = 'EDITION',
  COLLECTION_NAME = 'COLLECTION_NAME',
  FILE_NAME = 'FILE_NAME',
}

export interface ICollectionAttributeDto {
  name: string;
  value: string | number | boolean;
  type: CollectionAttributeType;
}

export interface ICreateProjectRarityDivisionDto {
  name: string;
  size: number;
  division: {
    original: number;
    rare: number;
    super_rare: number;
  };
}

export interface CollectionAttributeFormValue {
  name: string;
  value: string | number | boolean;
  type: CollectionAttributeType;
}

export interface CollectionConfig {
  path: string;
  name: string;
  groupByKey: string | number;
  layerConfig: LayerConfig[];
  rarityConfig: RarityDivisionConfig2;
  collectionCount: number;
  dnaPrefix: number;
  attributes: CollectionAttributeFormValue[];
}

export type DnaRarityMap = Record<RarityType, Array<string[]>>;

export interface SubCollectionExtended {
  subCollectionName: string;
  groupByKey: string | number;
  layers: LayerExtended[];
  rarityWeight: RarityWeight[];
  collectionCount: number;
  dnaRarityMap: DnaRarityMap;
  dnaPrefix: number;
  attributes: CollectionAttributeFormValue[];
}

export interface ImageElementSimple {
  name: string;
  file: File;
}

export interface ImageElement extends ImageElementSimple {
  id: string;
  type: string;
  isAttribute: boolean;
}

export interface LayerConfig {
  name: string;
  position: ImagePosition;
  size: ImageSize;
  isAttribute: boolean;
  isVisible: boolean;
}

export interface LayerExtended extends LayerConfig {
  subCollectionName: string;
  elements: ImageElement[]; // {id, name, path, isAttribute}[]
  elementIdsForRarity: Record<string, string[]>; // {super_rere: [id, id], rare: [id, id]},
}

export interface ImageSize {
  width: number;
  height: number;
}

export interface ImagePosition {
  x: number;
  y: number;
}

export enum RarityType {
  original = 'original',
  rare = 'rare',
  super_rare = 'super_rare',
}

export interface RarityDivision {
  [RarityType.original]: number;
  [RarityType.rare]: number;
  [RarityType.super_rare]: number;
}

export interface RarityDivisionConfig2 {
  [RarityType.original]: RarityInfo;
  [RarityType.rare]: RarityInfo;
  [RarityType.super_rare]: RarityInfo;
}

export interface RarityInfo {
  size: number;
  division: RarityDivision;
}

export interface RarityPercentInfo {
  id: RarityType;
  percent: number;
}

export type LayerName = string;

/**
 * @param _from - number in the edition to start this rarity from
 * @param _to - number in the edition to generate this rarity to
 */
export interface RarityWeight {
  value: RarityType;
  from: number;
  to: number;
  layerPercent: Record<LayerName, RarityPercentInfo[]>;
}

export type SubCollectionCountConfig = { from: number; to: number }[];

export type DNA = string[];
export type CID = string;

export interface ImageLayer extends LayerConfig {
  selectedElement: ImageElement & { rarity: RarityType };
}

export interface LayerWithImage {
  layer: ImageLayer;
  loadedImage: ImageBitmap;
}

export type AttributeDisplayType =
  | 'string'
  | 'number'
  | 'date'
  | 'boost_percentage'
  | 'boost_number';

export interface ArtMetaDataAttr {
  value: string | number | boolean;
  trait_type: string;
  display_type: AttributeDisplayType;
}

export interface IpfsArtMetadata {
  id: string;
  name: string;
  description: string;
  image: string;
  fileName: string;
  mime: string;
  external_url: string;
  edition: number;
  date: string;
  attributes: ArtMetaDataAttr[];
  creator?: string;
  external_link?: string;
  background_color?: string; // Hex color
  animation_url?: string;
  youtube_url?: string;
  properties?: Record<string, any>; // Additional properties

  // Royalty Information (optional)
  royalties?: RoyaltyInfo;
  // Collection Information (optional)
  collection?: {
    name: string;
    family?: string;
  };
}

// Interface for royalty information (optional)
export interface RoyaltyInfo {
  seller_fee_basis_points: number; // e.g., 500 for 5%
  fee_recipient: string; // Address receiving the royalties
}

export interface ArtWithMetaFormValue {
  art: File;
  meta: ArtMetaData;
  thumb: File;
}

export interface ArtMetaData extends IpfsArtMetadata {
  dna: string;
  groupByKey: string | number;
}

export interface ArtInfo {
  editionCount: number;
  newDna: DNA;
  attributesList: ArtMetaDataAttr[];
}

export interface OutputFiles {
  artFiles: File[];
  jsonFiles: string[];
}

export interface GlobalMetaData {
  metaDataCID: string;
  contractAddress: {
    rinkeby?: string;
    mainNet?: string;
  };
  createdAt: number;
  updatedAt: number;
  description: string;
  author: string;
  name: string;
  startsFrom: number;
  endsTo: number;
  collection: ArtMetaData[];
}
